import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { CurrencySwitcher } from './CurrencySwitcher';
import { Menu, X, User, LogOut, Settings, Award } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import NotificationBell from './NotificationBell';

export const Navbar = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, login, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Un guide (rôle guide) — même en attente de validation — accède à son
  // espace guide (création d'activités). On adapte la navigation en conséquence.
  const isHost = isAuthenticated && user && (user.role === 'guide' || user.role === 'admin');
  const homeDash = isHost ? '/guide/dashboard' : '/dashboard';
  const homeDashLabel = isHost ? t('nav.hostSpace') : t('nav.dashboard');

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="text-[#F9004D]">Guide</span>
              <span className="text-white">Match</span>
              <span className="text-[#00F0FF] text-sm ml-1">Tokyo</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-zinc-300 hover:text-white transition-colors">
              {t('nav.home')}
            </Link>
            <Link to="/activities" className="text-zinc-300 hover:text-white transition-colors">
              {t('nav.activities')}
            </Link>
            <Link to="/hosts" className="text-zinc-300 hover:text-white transition-colors">
              {t('nav.hosts')}
            </Link>
            {!isHost && (
              <Link to="/become-host" className="text-zinc-300 hover:text-white transition-colors">
                {t('nav.becomeHost')}
              </Link>
            )}

            <LanguageSwitcher />
            <CurrencySwitcher />

            {isAuthenticated ? (
              <>
                <Button
                  onClick={() => navigate(homeDash)}
                  variant="ghost"
                  className="text-zinc-300 hover:text-white"
                  data-testid="nav-dashboard-btn"
                >
                  {homeDashLabel}
                </Button>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-[#F9004D] text-white">
                          {user?.name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                    <DropdownMenuItem
                      onClick={() => navigate(homeDash)}
                      className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                    >
                      <User className="h-4 w-4 mr-2" />
                      {homeDashLabel}
                    </DropdownMenuItem>
                    {isHost && (
                      <DropdownMenuItem
                        onClick={() => navigate('/dashboard')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      >
                        <User className="h-4 w-4 mr-2" />
                        {t('nav.travelerSpace')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => navigate('/passeport')}
                      className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      data-testid="passeport-link"
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Mon Passeport
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/account')}
                      className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      data-testid="account-link"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Mon compte
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={logout}
                      className="text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
                      data-testid="logout-button"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className="text-zinc-300 hover:text-white"
                  data-testid="login-button"
                >
                  {t('nav.login')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4">
            <Link
              to="/"
              className="block text-zinc-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/activities"
              className="block text-zinc-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.activities')}
            </Link>
            <Link
              to="/hosts"
              className="block text-zinc-300 hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.hosts')}
            </Link>
            {!isHost && (
              <Link
                to="/become-host"
                className="block text-zinc-300 hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.becomeHost')}
              </Link>
            )}
            {isAuthenticated ? (
              <>
                <Link
                  to={homeDash}
                  className="block text-zinc-300 hover:text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {homeDashLabel}
                </Link>
                {isHost && (
                  <Link
                    to="/dashboard"
                    className="block text-zinc-300 hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.travelerSpace')}
                  </Link>
                )}
                <Link
                  to="/passeport"
                  className="block text-zinc-300 hover:text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Mon Passeport
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="block text-zinc-300 hover:text-white"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Button
                onClick={() => {
                  login();
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full"
              >
                {t('nav.login')}
              </Button>
            )}
            <LanguageSwitcher />
            <CurrencySwitcher />
          </div>
        )}
      </div>
    </nav>
  );
};